import type { CalendarDate } from '@internationalized/date';
import { Calendar } from '@heroui/react';
import type { AvailabilityWindow } from '../../../api/my-parking-spots';
import {
  getWindowRangePositionOnDate,
  getWindowsOnDate,
} from '../utils/availability-windows';

function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

const MULTI_DOT_COLORS = ['bg-warning', 'bg-accent', 'bg-success'] as const;

interface ActiveWindowsMonthCalendarProps {
  windows: AvailabilityWindow[];
  selectedDate: CalendarDate | null;
  highlightedWindowId: string | null;
  defaultFocusedValue?: CalendarDate;
  onSelectedDateChange: (date: CalendarDate) => void;
}

function WindowDayIndicators({ windowsOnDate }: { windowsOnDate: AvailabilityWindow[] }) {
  if (windowsOnDate.length === 0) return null;

  if (windowsOnDate.length === 1) {
    return <Calendar.CellIndicator className="bg-accent" />;
  }

  return (
    <div
      aria-hidden
      className="absolute bottom-1 left-1/2 flex -translate-x-1/2 gap-0.5"
    >
      {windowsOnDate.slice(0, 3).map((window, index) => (
        <span
          key={window.id}
          className={cn('size-1.5 shrink-0 rounded-full', MULTI_DOT_COLORS[index % MULTI_DOT_COLORS.length])}
        />
      ))}
    </div>
  );
}

export function ActiveWindowsMonthCalendar({
  windows,
  selectedDate,
  highlightedWindowId,
  defaultFocusedValue,
  onSelectedDateChange,
}: ActiveWindowsMonthCalendarProps) {
  const highlightedWindow = windows.find((window) => window.id === highlightedWindowId) ?? null;

  return (
    <Calendar
      aria-label="Calendário de janelas ativas"
      className="!w-full !max-w-none"
      value={selectedDate}
      defaultFocusedValue={defaultFocusedValue}
      onChange={onSelectedDateChange}
    >
      <Calendar.Header>
        <Calendar.YearPickerTrigger>
          <Calendar.YearPickerTriggerHeading />
          <Calendar.YearPickerTriggerIndicator />
        </Calendar.YearPickerTrigger>
        <Calendar.NavButton slot="previous" />
        <Calendar.NavButton slot="next" />
      </Calendar.Header>
      <Calendar.Grid>
        <Calendar.GridHeader>
          {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
        </Calendar.GridHeader>
        <Calendar.GridBody>
          {(date) => {
            const windowsOnDate = getWindowsOnDate(date, windows);
            const rangePosition = highlightedWindow
              ? getWindowRangePositionOnDate(date, highlightedWindow)
              : null;

            return (
              <Calendar.Cell date={date} className={cn(rangePosition && 'relative')}>
                {(cell) => (
                  <>
                    {rangePosition ? (
                      <span
                        aria-hidden
                        className={cn(
                          'pointer-events-none absolute inset-x-0 top-1/2 z-0 h-7 -translate-y-1/2 bg-accent/20',
                          rangePosition === 'start' && 'left-1 rounded-l-full',
                          rangePosition === 'end' && 'right-1 rounded-r-full',
                          rangePosition === 'middle' && 'rounded-none',
                          rangePosition === 'single' && 'left-1 right-1 rounded-full',
                        )}
                      />
                    ) : null}
                    {cell.formattedDate}
                    <WindowDayIndicators windowsOnDate={windowsOnDate} />
                  </>
                )}
              </Calendar.Cell>
            );
          }}
        </Calendar.GridBody>
      </Calendar.Grid>
      <Calendar.YearPickerGrid>
        <Calendar.YearPickerGridBody>
          {({ year }) => <Calendar.YearPickerCell year={year} />}
        </Calendar.YearPickerGridBody>
      </Calendar.YearPickerGrid>
    </Calendar>
  );
}
